import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FilterBar,
  ListEmpty,
  MoreInOtherMonths,
  NumberedPager,
  PAYERS,
  applySearch,
  inRange,
  paginate,
  rangeForPreset,
  sortBy,
  useListUrlState,
  windowLabel,
  type HistoryDatePreset,
} from '@/components/lists';
import { CLOSED_OUTCOME_LABEL, CLOSED_OUTCOME_OPTIONS, buildClosed } from '../historyMock';
import { money } from '../utils';

// The whole history, built once (deterministic). In production the server pages
// and searches this; here it lives in the browser only so the demo works (§8).
const ALL = buildClosed();
const PAGE_SIZE = 50;

/**
 * Pre-auths › Closed (00-ds-09 §9, D148): a history of used / expired / not sent
 * / denied pre-auths. Filter bar (search · date range · payer · outcome) over a
 * big mock, numbered pages of 50, default window the last 90 days. All state
 * lives in the URL (§6) so links are shareable and Back works.
 */
export function ClosedView() {
  const navigate = useNavigate();
  const [s, patch] = useListUrlState({ q: '', range: 'last-90', payer: '', outcome: '', page: '1' });
  const range = s.range as HistoryDatePreset;
  const label = windowLabel(range);

  const { pageRows, windowTotal, otherMonths, page } = useMemo(() => {
    const base = ALL.filter((row) => {
      if (s.payer && row.payer !== s.payer) return false;
      if (s.outcome && row.outcome !== s.outcome) return false;
      return true;
    });
    const searched = applySearch(base, s.q, (r) => r.searchText);
    const inWindow = searched.filter((r) => inRange(r.closedIso, rangeForPreset(range)));
    const sorted = sortBy(inWindow, (r) => r.closedIso, -1);
    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const clamped = Math.min(Math.max(1, Number(s.page) || 1), pageCount);
    return {
      pageRows: paginate(sorted, clamped, PAGE_SIZE),
      windowTotal: sorted.length,
      otherMonths: s.q.trim() ? searched.length - inWindow.length : 0,
      page: clamped,
    };
  }, [s.q, s.payer, s.outcome, s.page, range]);

  return (
    <div className="pa-wide">
      <FilterBar
        search={s.q}
        onSearch={(v) => { patch({ q: v, page: '1' }); }}
        searchPlaceholder="Search patient, payer, reference or amount"
        dateRange={range}
        onDateRange={(v) => { patch({ range: v, page: '1' }); }}
      >
        <select
          className="lx-select"
          aria-label="Payer"
          value={s.payer}
          onChange={(e) => { patch({ payer: e.target.value, page: '1' }); }}
        >
          <option value="">All payers</option>
          {PAYERS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className="lx-select"
          aria-label="Outcome"
          value={s.outcome}
          onChange={(e) => { patch({ outcome: e.target.value, page: '1' }); }}
        >
          <option value="">Any outcome</option>
          {CLOSED_OUTCOME_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </FilterBar>

      <section className="pa-card" aria-labelledby="pa-closed-title">
        <div className="pa-card-head">
          <h2 className="pa-h2" id="pa-closed-title">Closed pre-auths</h2>
          <span className="pa-meta">
            Used, expired, not sent or denied. Every row opens the pre-auth’s full record. Showing {label}.
          </span>
        </div>

        {windowTotal === 0 ? (
          <ListEmpty
            {...(s.q ? { query: s.q } : { message: `No closed pre-auths in ${label}.` })}
            windowLabel={label}
            {...(range !== 'all-time'
              ? { onSearchAllTime: () => { patch({ range: 'all-time', page: '1' }); } }
              : {})}
          />
        ) : (
          <>
            <div className="pa-tbl-wrap">
              <table className="pa-tbl">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Reference</th>
                    <th className="ta-right">Payer’s estimate</th>
                    <th>Closed</th>
                    <th>What happened</th>
                    <th className="ta-right">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((r) => {
                    const badge = CLOSED_OUTCOME_LABEL[r.outcome];
                    // A used pre-auth links to its permanent claim record; every
                    // other outcome opens the read-only pre-auth (§5.4).
                    const openRow = () => {
                      if (r.outcome === 'used') void navigate(`/claims/record/${r.id}`);
                      else void navigate(`/pre-auths/${r.id}`);
                    };
                    return (
                      <tr key={r.id} className="pa-row-open" onClick={openRow} style={{ cursor: 'pointer' }}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{r.patient}</div>
                          <div className="pa-meta">{r.payer} · {r.code} {r.procedure} {r.tooth}</div>
                        </td>
                        <td className="num">{r.reference || '—'}</td>
                        <td className="ta-right num">{money(r.estimate)}</td>
                        <td>{r.closedLabel}</td>
                        <td style={{ color: 'var(--fg2)', maxWidth: 340 }}>{r.sentence}</td>
                        <td className="ta-right">
                          <span className={`pa-badge ${badge.cls}`}>{badge.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {otherMonths > 0 ? (
              <MoreInOtherMonths count={otherMonths} onShowAllTime={() => { patch({ range: 'all-time', page: '1' }); }} />
            ) : null}

            <NumberedPager
              page={page}
              pageSize={PAGE_SIZE}
              total={windowTotal}
              onPage={(p) => { patch({ page: String(p) }); }}
            />
          </>
        )}
      </section>
    </div>
  );
}
